/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

/* tslint:disable:max-func-body-length */

import {
    getPayloadObject,
    // getTelemetryAttributes,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { DeliveryOption, OrgUnitLocation, SimpleProduct, StoreHours } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

// import { PickUpOptionList } from '../pickup-option-list';
import { buildStoreHours, IHoursDisplayInfo, secondsToTime } from './date-time-helpers';
import { IDobbiesBuyboxStoreSelectorConfig } from '../../dobbies-buybox-store-selector.props.autogenerated';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';

export interface IStoreSelectorLocationLineItemResources {
    contactInfoHeader: string;
    storeHoursHeader: string;
    availabilityInfoHeader: string;

    closedText: string;

    outOfStock: string;
    inStock: string;

    currentLocation: string;
    selectLocation: string;
    selectLocationAriaLabelFormat: string;
    setAsPreferredStoreText: string;
    setAsPreferredStoreTextAriaLabel: string;
    preferredStoreText: string;
    preferredStoreAriaLabel: string;
    quantityText: string;

    days: {
        monday: string;
        tuesday: string;
        wednesday: string;
        thursday: string;
        friday: string;
        saturday: string;
        sunday: string;
        mondayFull: string;
        tuesdayFull: string;
        wednesdayFull: string;
        thursdayFull: string;
        fridayFull: string;
        saturdayFull: string;
        sundayFull: string;
    };
}

export interface IStoreSelectorLocationLineItemProps {
    location: OrgUnitLocation;
    storeHours?: StoreHours;

    hideStockStatus?: boolean;
    isInStock?: boolean;
    isCurrentLocation?: boolean;

    resources: IStoreSelectorLocationLineItemResources;
    stockStatusLabel?: string;
    storeLocatorView?: boolean;
    isPreferredStoreEnabled?: boolean;
    index: string;
    preferredStoreLocationId: string | null;
    storePickUpOptionList?: DeliveryOption[];
    productPickUpOptionList?: DeliveryOption[];
    showQuantity?: boolean;
    showStoreAvailableQuantity?: number;
    filteredPickupMode?: string;
    isDisplayProduct: boolean;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    preferredDistanceUnit: string;
    isLocationDisabled?: boolean;
    handlers: {
        onSelected(location: OrgUnitLocation, deliveryMode?: string): void;
        onSetAsPreferredStore(locationId: string | undefined): void;
        onRemovePreferredStore(): void;
    };
    showCnC?: boolean;
    CncPickupIds: (number | undefined)[];
    localStoreHeading: string;
    config: IDobbiesBuyboxStoreSelectorConfig;
    isSelected: boolean;
    context?: ICoreContext<IGeneric<IAny>>;
    product?: SimpleProduct;
}

const StoreSelectorLocationLineItemComponentActions = {
    onLocationSelected(props: IStoreSelectorLocationLineItemProps, deliveryMode?: string): void {
        props.handlers.onSelected(props.location, deliveryMode);
    },
    onSetAsPreferredStore(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onSetAsPreferredStore(props.location.OrgUnitNumber);
    },
    onRemovePreferredStore(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onRemovePreferredStore();
    }
};

const renderStoreHours = (
    storeHours: StoreHours | undefined,
    location: OrgUnitLocation,
    resources: IStoreSelectorLocationLineItemResources
): JSX.Element | null => {
    // First, use the store hours. This will allow listing day by day houts
    if (storeHours && storeHours.RegularStoreHours) {
        const storeHoursList: IHoursDisplayInfo[] = buildStoreHours(storeHours.RegularStoreHours, resources);

        return (
            <div className='ms-store-select__store-hours-details'>
                <div>
                    {storeHoursList?.map(displayTime => (
                        <div className='d-inline-flex' key={JSON.stringify(displayTime)}>
                            {displayTime.endDayShort ? (
                                <p className='ms-store-select__store-hours-day'>
                                    <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>–
                                    <abbr title={displayTime.endDayFull}>{displayTime.endDayShort}</abbr>
                                </p>
                            ) : (
                                <p className='ms-store-select__store-hours-day'>
                                    <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>
                                </p>
                            )}
                            <p className='ms-store-select__store-hours-time'>({displayTime.timeRange})</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // If that fails, fall back to using the OpenFrom and OpenTo properties on the location
    const storeOpenFrom: string | undefined = secondsToTime(location.OpenFrom);
    const storeOpenTo: string | undefined = secondsToTime(location.OpenTo);

    return (
        <div className='ms-store-select__store-hours-details'>
            {storeOpenFrom}-{storeOpenTo}
        </div>
    );
};

export const buildDistanceString = (
    distance: number | undefined,
    preferredDistanceUnit: string,
    isLocationDisabled?: boolean
): string | undefined => {
    if (distance === undefined || isLocationDisabled) {
        return undefined;
    } else if (distance < 1) {
        return `(<1 ${preferredDistanceUnit})`;
    }
    return `(${Math.floor(distance)} ${preferredDistanceUnit})`;
};

// /**
//  * Function to store contact.
//  * @param contacts - Store location within radius.
//  * @returns - Return primary contact number of store.
//  */
//  const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
//     if (!contacts) {
//         return undefined;
//     }
//     const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === 1 && !contact.IsPrivate);
//     if (allPhoneContacts && allPhoneContacts.length > 0) {
//         const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);
//         if (primaryPhoneContact) {
//             return primaryPhoneContact.Locator;
//         }
//         return allPhoneContacts[0].Locator;
//     }
//     return undefined;
// };

/**
 * On Store locator click functionality.
 * @param props -Store locator location items.
 * @returns Store locator selected location line item action.
 */

const onPickupInStoreClickHandler = (props: IStoreSelectorLocationLineItemProps) => () => {
    StoreSelectorLocationLineItemComponentActions.onLocationSelected(props);
};
/**
 * On Store locator click functionality.
 * @param props -Store locator location items.
 * @param pickupMode -Store locator location items.
 * @returns Store locator selected location line item action.
 */

// const onPickupModeSelectHandler = (props: IStoreSelectorLocationLineItemProps, pickupMode?: string) => () => {
//     StoreSelectorLocationLineItemComponentActions.onLocationSelected(props, pickupMode);
// };
/**
 * Set Preferred store functionality.
 * @param props -Store locator location items.
 * @returns Store locator selected location line item action.
 */

// const onSetPreferredStoreClickHandler = (props: IStoreSelectorLocationLineItemProps) => () => {
//     StoreSelectorLocationLineItemComponentActions.onSetAsPreferredStore(props);
// };
/**
 * Remove preferred store functionality.
 * @param props -Store locator location items.
 * @returns Store locator selected location line item action.
 */

// const removePreferredStoreHandler = (props: IStoreSelectorLocationLineItemProps) => () => {
//     StoreSelectorLocationLineItemComponentActions.onRemovePreferredStore(props);
// };
/**
 * On Change functionality.
 * @param props -Store locator location items.
 * @returns Store locator selected location line item action.
 */

// const onChangeHandler = (props: IStoreSelectorLocationLineItemProps) => async (deliveryCode: string): Promise<void> => {
//     StoreSelectorLocationLineItemComponentActions.onLocationSelected(props, deliveryCode);
//     return Promise.resolve();
// };
/**
 * Method to render pickup modes.
 * @param props - StoreSelector Line item props.
 * @param deliveryOptionList - Product DeliveryOption List.
 * @param pickupInStoreButtonText - Pickup store button text.
 * @param pickupInStoreAriaLabel - Pickup store aria label.
 * @returns Jsx component.
 */

// const renderPickupModes = (
//     props: IStoreSelectorLocationLineItemProps,
//     deliveryOptionList: DeliveryOption[],
//     pickupInStoreButtonText: string,
//     pickupInStoreAriaLabel: string
// ): JSX.Element | null => {
//     if (props.filteredPickupMode === undefined) {
//         return (
//             <PickUpOptionList
//                 pickupListOptionMenuText={pickupInStoreButtonText}
//                 pickupmodeList={deliveryOptionList}
//                 onChange={onChangeHandler(props)}
//             />
//         );
//     }
//     return props.showCnC ? (
//         <button
//             className='ms-store-select__location-line-select-store'
//             aria-label={pickupInStoreAriaLabel}
//             onClick={onPickupModeSelectHandler(props, props.filteredPickupMode)}
//         >
//             {pickupInStoreButtonText}
//         </button>
//     ) : null;
// };

const StoreSelectorLocationLineItem: React.FC<IStoreSelectorLocationLineItemProps> = (props: IStoreSelectorLocationLineItemProps) => {
    // const onClick = () => {
    //     StoreSelectorLocationLineItemComponentActions.onLocationSelected(props);
    // };

    // const onChange = async (deliveryCode: string): Promise<void> => {
    //     StoreSelectorLocationLineItemComponentActions.onLocationSelected(props, deliveryCode);
    // };

    const {
        // hideStockStatus,
        // isCurrentLocation,
        isInStock,
        location,
        resources,
        storeHours,
        // storeLocatorView,
        // index,
        // preferredStoreLocationId,
        // isPreferredStoreEnabled,
        // isLocationDisabled,
        telemetryContent,
        // storePickUpOptionList,
        // productPickUpOptionList,
        // CncPickupIds,
        localStoreHeading
    } = props;

    const storeName: string | undefined = location.OrgUnitName;
    const storeNameHref: string | undefined = `${storeName?.toLowerCase().replace(/\s+/g, '-')}`;
    // const distanceAsString: string | undefined = buildDistanceString(location.Distance, props.preferredDistanceUnit, isLocationDisabled);
    const storeAddress: string | undefined = location.Address;

    // const canSelectLocation = hideStockStatus || isInStock;
    // const pickupInStoreButtonText: string = resources.selectLocation;
    // const pickupInStoreAriaLabel: string = resources.selectLocationAriaLabelFormat.replace('{StoreName}', storeName || '');
    // const isPreferredStore = preferredStoreLocationId === location.OrgUnitNumber;
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    // const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.PreferredStore;
    // const preferredStoreAttr = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.RemovePreferredStore;
    // const RemovePreferredStoreAttr = getTelemetryAttributes(telemetryContent!, payLoad);
    // const deliveryOptionList = matchDeliveryOptions(productPickUpOptionList, storePickUpOptionList);
    // const [isHover, setIsHover] = React.useState(false);

    /* VSI Customization - START - Remove GBR From your Shipping Address */
    const storeAddressText = storeAddress
        ?.replace(/\nGBR$/, '')
        .replace(/\n/g, ', ')
        .trim();
    /* VSI Customization - END */

    /* VSI Customization - START -  Check Stock Store Status-  */
    const stockStatus = props.stockStatusLabel;

    /* VSI Customization - END */

    return (
        <div className='ms-store-select__location-line-item'>
            <div className='ms-store-select__location-line-item-header'>
                {isInStock && (
                    <div className='ms-store-select__location-select custom-radio'>
                        <input className='delivery-radio' type='radio' checked={props.isSelected} name='store-location' />
                        <div className='custom-radio-mark'></div>
                    </div>
                )}
                <span className='ms-store-select__location-line-item-store-name'>
                    {props.config.storeNamePrefix ? `${props.config.storeNamePrefix}${storeName}` : storeName}
                </span>
            </div>

            <div className='ms-store-select__location-line-item-content'>
                <div className='ms-store-select__location-line-item-contact-info'>
                    <p className='ms-store-select__location-line-shop-address-text'>{storeAddressText}</p>
                </div>

                <div className='ms-store-select__store-hours-details'>
                    {renderStoreHours(storeHours, location, resources)}
                    <a href={`/${storeNameHref}`} className='ms-store-select__store-hours-more-info'>
                        Store info
                    </a>
                </div>

                {isInStock ? (
                    <div className='ms-store-select__location-line-item-store-availability'>
                        {!localStoreHeading.includes('Check stock') && (
                            <p className='ms-store-select__location-stock-status'>Available for click & collect</p>
                        )}

                        {localStoreHeading.includes('Check stock') && stockStatus !== 'Low Stock' ? (
                            <p className='ms-store-select__location-stock-status'>Available in store</p>
                        ) : null}
                        {stockStatus === 'Low Stock' && localStoreHeading.includes('Check stock') && (
                            <div className='ms-store-select__location-line-item-store-availability'>
                                <p className='ms-store-select__location-stock-status low-stock'>Low stock in store</p>
                            </div>
                        )}
                        {stockStatus === 'Low Stock' && !localStoreHeading.includes('Check stock') && (
                            <p className='ms-store-select__location-stock-few'>Hurry, just a few left - order soon!</p>
                        )}
                    </div>
                ) : (
                    <div className='ms-store-select__location-line-item-store-availability'>
                        {!localStoreHeading.includes('Check stock') ? (
                            <p className='ms-store-select__location-stock-status'>Not available for click & collect</p>
                        ) : (
                            <p className='ms-store-select__location-stock-status'>Not available in store</p>
                        )}
                    </div>
                )}

                {isInStock && !localStoreHeading.includes('Check stock') && (
                    <div className='ms-store-select__location-select-store'>
                        <button className='msc-add-to-cart' onClick={onPickupInStoreClickHandler(props)}>
                            Select store
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export const matchDeliveryOptions = (
    productdeliveryOptions: DeliveryOption[] | undefined,
    storePickUpOptionList: DeliveryOption[] | undefined
): DeliveryOption[] => {
    const deliveryOption: DeliveryOption[] = [];
    productdeliveryOptions?.map(delivery => {
        const pickup = storePickUpOptionList?.find(_delivery => _delivery.Code === delivery.Code);
        if (pickup) {
            deliveryOption.push(pickup);
        }
    });

    return deliveryOption;
};

export const StoreSelectorLocationLineItemComponent = StoreSelectorLocationLineItem as (
    props: IStoreSelectorLocationLineItemProps
) => JSX.Element;
